import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiTextField from "@mui/material/TextField";
import MuiFormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TableFooter from "@mui/material/TableFooter";
import Chip from "@mui/material/Chip";

import SearchIcon from "@mui/icons-material/Search";
import CachedIcon from "@mui/icons-material/Cached";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import DoneOutlineRounded from "@mui/icons-material/DoneOutlineRounded";

import Modal from "../components/Modal";
import api from "../service/api";
import { setError } from "../reducers/appSlice";

const FormControl = styled(MuiFormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: 200,
}));

const TextField = styled(MuiTextField)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const ModalButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

var validationSchema = Yup.object().shape(
  {
    id: Yup.number()
      .typeError("Solo números")
      .when(["descOrgCompleta", "descOrgPadre", "idMinisterio"], {
        is: (descOrgCompleta, descOrgPadre, idMinisterio) =>
          !descOrgCompleta && !descOrgPadre && !idMinisterio,
        then: Yup.number()
          .required("Al menos un filtro es requerido")
          .typeError("Solo números"),
      }),
    descOrgCompleta: Yup.string().when(["id", "descOrgPadre", "idMinisterio"], {
      is: (id, descOrgPadre, idMinisterio) =>
        !id && !descOrgPadre && !idMinisterio,
      then: Yup.string().required("Al menos un filtro es requerido"),
    }),
    descOrgPadre: Yup.string().when(["id", "descOrgCompleta", "idMinisterio"], {
      is: (id, descOrgCompleta, idMinisterio) =>
        !id && !descOrgCompleta && !idMinisterio,
      then: Yup.string().required("Al menos un filtro es requerido"),
    }),
    idMinisterio: Yup.number()
      .typeError("Solo números")
      .when(["id", "descOrgCompleta", "descOrgPadre"], {
        is: (id, descOrgCompleta, descOrgPadre) =>
          !id && !descOrgCompleta && !descOrgPadre,
        then: Yup.number()
          .required("Al menos un filtro es requerido")
          .typeError("Solo números"),
      }),
  },
  [
    ["id", "descOrgCompleta"],
    ["id", "descOrgPadre"],
    ["id", "idMinisterio"],
    ["descOrgCompleta", "descOrgPadre"],
    ["descOrgCompleta", "idMinisterio"],
    ["idMinisterio", "descOrgPadre"],
  ]
);

const TablePaginationActions = (props) => {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const BuscadorOrganismosMulti = (props) => {
  const dispatch = useDispatch();
  const [datos, setDatos] = useState([]);
  const [ministerios, setMinisterios] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [seleccionados, setSelecionados] = useState([]);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.handleReset();
    setSelecionados([]);
    setOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeTextField = (event) => {
    if (/^\d*$/.test(event.target.value)) {
      formik.setFieldValue(event.target.id, event.target.value);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: "",
      descOrgCompleta: "",
      descOrgPadre: "",
      idMinisterio: "",
      activo: "S",
      accion: props.accion,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setDatos([]);
      setPage(0);
      setSubmitting(true);
      api
        .buscarOrganismo(values)
        .then((res) => {
          setDatos(res.data);
        })
        .catch((e) => {
          dispatch(setError(e.response.data.error));
          console.log("Error al cargar los datos");
        })
        .finally(() => setSubmitting(false));
    },
    onReset: () => {
      setDatos([]);
    },
  });

  const columns = [
    {
      id: "id",
      label: "Código",
      minWidth: 100,
      align: "right",
    },

    {
      id: "descOrgCompleta",
      label: "Organismo",
      minWidth: 300,
      align: "left",
    },
    {
      id: "acciones",
      label: "Acciones",
      minWidth: 100,
      align: "center",
    },
  ];

  const agregarOrganismo = (organismo) => {
    if (seleccionados.some((elemento) => elemento.id === organismo.id)) return;
    setSelecionados([...seleccionados, organismo]);
    // props.resultado(dato);
    // handleClose();
  };

  const quitarOrganismo = (organismo) => {
    const nuevo = seleccionados.filter(
      (elemento) => elemento.id !== organismo.id
    );
    setSelecionados(nuevo);
  };

  const generarLabelOrganismo = (organismo) => {
    const nombre = organismo.descOrgCompleta.split(" - ")[0];
    return `${organismo.id} - ${nombre}`;
  };

  const handleConfirmar = () => {
    props.resultado(seleccionados);
    handleClose();
  };

  useEffect(() => {
    async function listarMinisterios() {
      try {
        let res = await api.listarMinisterios({});
        setMinisterios(res.data);
      } catch (e) {
        dispatch(setError("Error al cargar los datos"));
      }
    }

    listarMinisterios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {props.mostrar === "boton" ? (
        <ModalButton
          variant="plata"
          onClick={handleOpen}
          endIcon={<SearchIcon />}
        >
          Organismo
        </ModalButton>
      ) : (
        <IconButton
          title="Buscar organismos"
          color="primary"
          onClick={handleOpen}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      )}
      <Modal
        open={open}
        handleClose={handleClose}
        title="BUSCADOR DE ORGANISMOS"
        responsiveTitle="ORGANISMOS"
        // closeButton
      >
        <form
          id="buscador"
          name="buscador"
          onSubmit={formik.handleSubmit}
          onReset={formik.handleReset}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="id"
                name="id"
                label="Código"
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
                value={formik.values.id}
                onChange={handleChangeTextField}
                error={formik.touched.id && Boolean(formik.errors.id)}
                helperText={formik.touched.id && formik.errors.id}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                error={
                  formik.touched.idMinisterio &&
                  Boolean(formik.errors.idMinisterio)
                }
              >
                <InputLabel id="idMinisterioLabel">Ministerio</InputLabel>
                <Select
                  labelId="idMinisterioLabel"
                  id="idMinisterio"
                  name="idMinisterio"
                  variant="outlined"
                  label="Ministerio"
                  value={formik.values.idMinisterio}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="" selected disabled>
                    Seleccione
                  </MenuItem>
                  {ministerios.map((ministerio) => (
                    <MenuItem value={ministerio.id} key={ministerio.id}>
                      {ministerio.descripcion}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.idMinisterio && formik.errors.idMinisterio}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="descOrgCompleta"
                name="descOrgCompleta"
                label="Organismo"
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
                value={formik.values.descOrgCompleta}
                onChange={formik.handleChange}
                error={
                  formik.touched.descOrgCompleta &&
                  Boolean(formik.errors.descOrgCompleta)
                }
                helperText={
                  formik.touched.descOrgCompleta &&
                  formik.errors.descOrgCompleta
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="descOrgPadre"
                name="descOrgPadre"
                label="Organismo superior"
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
                value={formik.values.descOrgPadre}
                onChange={formik.handleChange}
                error={
                  formik.touched.descOrgPadre &&
                  Boolean(formik.errors.descOrgPadre)
                }
                helperText={
                  formik.touched.descOrgPadre && formik.errors.descOrgPadre
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "none", md: "inline" } }}
            >
              <Button
                variant="plata"
                type="reset"
                endIcon={<CachedIcon />}
                sx={{ m: 1 }}
              >
                {"Limpiar"}
              </Button>{" "}
              <Button
                sx={{ m: 1 }}
                variant="verde"
                color="primary"
                type="submit"
                disabled={submitting}
                endIcon={<SearchIcon />}
              >
                {submitting ? "Buscando..." : "Buscar"}
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "block", md: "none" } }}
            >
              <IconButton color="primary" type="reset" title="Limpiar">
                <CachedIcon />
              </IconButton>{" "}
              <IconButton
                color="primary"
                type="submit"
                disabled={submitting}
                title="Buscar"
              >
                <SearchIcon />
              </IconButton>
            </Grid>

            <Grid item xs={12} sm={12}>
              {" "}
            </Grid>
          </Grid>
        </form>

        <TableContainer sx={{ maxHeight: 440 }}>
          <Table size="small" sx={{ border: "1px solid #dee2e6" }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    sx={{
                      backgroundColor: "#fff",
                      color: "#3c4b64",
                      minWidth: column.minWidth,
                    }}
                    key={column.id}
                    align={column.align}
                  >
                    <b>{column.label}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {datos
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dato, index) => {
                  return (
                    <TableRow hover tabIndex={-1} key={index}>
                      {columns.map((column) => {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.id === "acciones" ? (
                              <IconButton
                                title="Seleccionar"
                                onClick={() => agregarOrganismo(dato)}
                              >
                                <DoneOutlineRounded sx={{ fontSize: 14 }} />
                              </IconButton>
                            ) : (
                              dato[column.id]
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              {submitting ? (
                <TableRow>
                  <TableCell colSpan="7" align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : null}
              {datos.length === 0 && !submitting ? (
                <TableRow>
                  <TableCell colSpan="7" align="center">
                    <Typography variant="aclaracion">
                      No hay datos para mostrar
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[
                    5,
                    10,
                    25,
                    { label: "Todas", value: datos.length },
                  ]}
                  colSpan={columns.length}
                  count={datos.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      "aria-label": "filas pr página",
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        {/* <Stack direction="row" spacing={1} useFlexGap>
          {seleccionados.map((organismo, index) => {
            return <Chip color="primary" label={generarLabelOrganismo(organismo)} onDelete={() => quitarOrganismo(organismo)} />
          })}
        </Stack> */}
        <br></br>
        <Typography variant="cuerpo">
          {"ORGANISMOS: "}
          {seleccionados.length === 0 && (
            <Typography variant="aclaracion">
              {"No hay organismos seleccionados"}
            </Typography>
          )}
        </Typography>
        <Grid container spacing={1} sx={{ py: 1 }}>
          {seleccionados.map((organismo, index) => {
            return (
              <Grid key={organismo.id} item>
                <Chip
                  color="primary"
                  variant="outlined"
                  label={generarLabelOrganismo(organismo)}
                  onDelete={() => quitarOrganismo(organismo)}
                />
              </Grid>
            );
          })}
        </Grid>
        <Box sx={{ display: "flex", flexDirection: "row-reverse" }}>
          <Box>
            <Button
              variant="gris"
              type="button"
              sx={{ m: 1 }}
              onClick={handleConfirmar}
              disabled={seleccionados.length === 0}
              // endIcon={<SearchIcon />}
            >
              {"Confirmar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default BuscadorOrganismosMulti;
